// Lightweight dataLayer push helpers. The app only pushes clean data;
// GTM (container GTM-MPTLJVSC) decides what becomes a GA4 event.
// See the GTM/GA4 setup notes handed over with this work.

import { captureWhatsAppClickLead } from './leads'
import { ctaContextKey, getCtaClickContext } from './cta-measurement'

type DataLayerParams = Record<string, unknown>

declare global {
  interface Window {
    dataLayer?: DataLayerParams[]
    // Defined by the Consent Mode bootstrap inline script in __root.tsx.
    gtag?: (...args: unknown[]) => void
  }
}

/** GA4 ecommerce item. Keep keys aligned with GA4's recommended schema. */
export type GA4Item = {
  item_id: string | number
  item_name: string
  item_category?: string
  item_list_name?: string
  price?: number | string | null
  index?: number
  quantity?: number
}

/**
 * Push a plain event to the GTM dataLayer. No-op during SSR.
 */
export function track(event: string, params: DataLayerParams = {}): void {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ event, ...params })
}

/**
 * Push a GA4 ecommerce event. Clears the previous `ecommerce` object first so
 * item data never bleeds between events (GA4 best practice with GTM).
 */
export function trackEcommerce(
  event: string,
  ecommerce: DataLayerParams,
  extra: DataLayerParams = {},
): void {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({ event, ecommerce, ...extra })
}

export function trackViewItem(item: GA4Item, currency = 'IDR'): void {
  trackEcommerce('view_item', { currency, value: Number(item.price) || 0, items: [item] })
}

export function trackViewItemList(listName: string, items: GA4Item[]): void {
  trackEcommerce('view_item_list', {
    item_list_name: listName,
    items: items.map((it, index) => ({ ...it, index, item_list_name: listName })),
  })
}

export function trackSelectItem(item: GA4Item, listName: string): void {
  trackEcommerce('select_item', {
    item_list_name: listName,
    items: [{ ...item, item_list_name: listName }],
  })
}

export type NewsAnalyticsItem = {
  id: string | number
  title: string
  slug: string
  published_at?: string | null
}

export function toNewsGA4Item(item: NewsAnalyticsItem, index?: number): GA4Item {
  return {
    item_id: item.id,
    item_name: item.title,
    item_category: 'Berita',
    ...(index == null ? {} : { index }),
  }
}

/** Records which news cards were rendered in a named editorial surface. */
export function trackNewsListView(listName: string, items: NewsAnalyticsItem[]): void {
  if (items.length === 0) return
  trackViewItemList(listName, items.map((item, index) => toNewsGA4Item(item, index)))
}

/** Records a deliberate click from a news list to an article. */
export function trackNewsSelect(item: NewsAnalyticsItem, listName: string, index?: number): void {
  trackSelectItem(toNewsGA4Item(item, index), listName)
}

export function trackArticleView(item: NewsAnalyticsItem, category: 'Berita' | 'Blog'): void {
  track('article_view', {
    article_id: item.id,
    article_title: item.title,
    article_slug: item.slug,
    article_category: category,
    publish_date: item.published_at || undefined,
  })
}

export function trackArticleProgress(item: NewsAnalyticsItem, category: 'Berita' | 'Blog', percentScrolled: number): void {
  track('article_progress', {
    article_id: item.id,
    article_title: item.title,
    article_slug: item.slug,
    article_category: category,
    percent_scrolled: percentScrolled,
  })
}

export function trackNewsArticleView(item: NewsAnalyticsItem): void {
  trackArticleView(item, 'Berita')
}

export function trackNewsArticleProgress(item: NewsAnalyticsItem, percentScrolled: number): void {
  trackArticleProgress(item, 'Berita', percentScrolled)
}

export function trackSearch(searchTerm: string, results?: number): void {
  track('search', {
    search_term: searchTerm,
    ...(results != null ? { search_results: results } : {}),
  })
}

export function trackVideoStart(videoTitle?: string): void {
  track('video_start', videoTitle ? { video_title: videoTitle } : {})
}

export function trackContact(method: 'email' | 'phone'): void {
  track('contact', { method })
}

export type LeadSource =
  | 'sitewide'
  | 'home'
  | 'product_page'
  | 'category'
  | 'catalog'
  | 'landing_page'
  | 'reseller'
  | 'ebook'
  | 'minimum_order'
  | 'search'
  | 'brand'
  | 'blog'
  | 'news'

export type WhatsAppLeadParams = {
  /** Stable position of the CTA within its page or component. */
  cta_placement: string
  /** Product family, landing-page offer, or category shown beside the CTA. */
  lp_category?: string
  /** Flat event parameter used by the GA4 Lead Item Name custom dimension. */
  lead_item_name?: string
  /** Optional ecommerce context retained for item-aware explorations. */
  items?: GA4Item[]
  search_term?: string
  sell_channel?: string
}

/**
 * A WhatsApp click is the primary conversion for this wholesale business.
 * Mapped to GA4's recommended `generate_lead` event so it slots into the
 * built-in lead reports. Mark `generate_lead` as a Key Event in GA4.
 */
export function trackWhatsAppLead(
  source: LeadSource,
  params: WhatsAppLeadParams,
): void {
  track('generate_lead', {
    method: 'whatsapp',
    lead_source: source,
    currency: 'IDR',
    lp_category: '',
    lead_item_name: '',
    search_term: '',
    sell_channel: '',
    measurement_version: '',
    page_type: '',
    cta_first_click: 'untracked',
    cta_viewed: 'untracked',
    seconds_to_lead: null,
    lead_scroll_percent: null,
    ...params,
    ...getCtaClickContext({ lead_source: source, ...params }),
  })
  // Mirror the click into Whatzap (first-party CRM) as a phone-less beacon so
  // incoming chats can be reconciled with the ad click that produced them.
  captureWhatsAppClickLead(source)
}

/** Keep impression metadata and the existing conversion handler together. */
export function whatsAppCtaProps(source: LeadSource, params: WhatsAppLeadParams) {
  return {
    'data-wa-cta': ctaContextKey({ lead_source: source, ...params }),
    onClick: () => trackWhatsAppLead(source, params),
  }
}

// ─── Consent Mode v2 ───────────────────────────────────────────────────────────
// Default state is set by the bootstrap script in __root.tsx (granted globally,
// denied only in EEA/UK regions). The banner calls these to record a choice.

export const CONSENT_STORAGE_KEY = 'dropify_consent_v1'

const GRANTED = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
} as const

const DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
} as const

function updateConsent(granted: boolean): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('consent', 'update', granted ? GRANTED : DENIED)
}

export function acceptConsent(): void {
  updateConsent(true)
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'granted')
  } catch {
    /* storage unavailable — consent applies for this session only */
  }
}

export function rejectConsent(): void {
  updateConsent(false)
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, 'denied')
  } catch {
    /* storage unavailable */
  }
}

/** Returns the stored choice, or null if the visitor hasn't decided yet. */
export function getStoredConsent(): 'granted' | 'denied' | null {
  if (typeof window === 'undefined') return null
  try {
    const value = window.localStorage.getItem(CONSENT_STORAGE_KEY)
    return value === 'granted' || value === 'denied' ? value : null
  } catch {
    return null
  }
}
